import { ListItemModel } from 'Data/ViewModels/ListItemModel';
import { hasEmptyValue, hasNonEmptyValue, hasValue } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import classNames from 'classnames';
import { isNil } from 'ramda';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../Data/ReduxViewModels/WorksheetEditor';
import { toTitleCase } from '../../../../Lib/String';
import { Button, ButtonGroup, ConfirmDialog, Field, FixedOptionTextOutput, RadioGroup } from '../../../Core';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { FieldSet, ListFieldSection } from '../../../Layout';
import { IWorksheetComponent } from '../types';
import './WorksheetAssociatedTestSection.scss';
import WorksheetExternalTestSection, { IWorksheetExternalTestSectionProps } from './WorksheetExternalTestSection';
import WorksheetImportedTestSection, { IWorksheetImportedTestSectionProps } from './WorksheetImportedTestSection';
import WorksheetAutoImportedTestSection from './WorksheetImportedTestSection/WorksheetAutoImportedTestSection';
import WorksheetMeasuredTestSection, { IWorksheetMeasuredTestSectionProps } from './WorksheetMeasuredTestSection';
import { testTypeStartedFunctionMap } from './testTypeStartedFunctionMap';
import { AssociatedTestType, AssociatedTestTypes } from './types';

interface IWorksheetAssociatedTestModel {
  measured?: boolean | Partial<IWorksheetMeasuredTestSectionProps>;
  imported?: Partial<IWorksheetImportedTestSectionProps>;
  autoImported?: boolean | Partial<IWorksheetImportedTestSectionProps>;
  external?: Partial<IWorksheetExternalTestSectionProps>;
}

interface IWorksheetAssociatedTestOptionsLabels {
  measured?: string;
  imported?: string;
  autoImported?: string;
  external?: string;
}

interface IWorksheetAssociatedTestSectionProps extends IWorksheetComponent {
  associatedTestModel: IWorksheetAssociatedTestModel;
  optionsLabels?: IWorksheetAssociatedTestOptionsLabels;
  disabled?: boolean;
  externalTestResults?: React.ReactNode;
  heading?: string;
  importedTestResults?: React.ReactNode;
  label?: string;
  name: string;
  testType: string;
  visible?: boolean;
  /**
   * Controls whether the associated test section will be indented and shaded (true by default)
   *
   * Only recommended to be set false for measured-only tests to be embedded directly into a worksheet
   */
  indented?: boolean;
  defaultSelection?: AssociatedTestType;
}

let WorksheetAssociatedTestSection: React.FC<IWorksheetAssociatedTestSectionProps> = ({
  associatedTestModel,
  disabled,
  optionsLabels,
  heading,
  label,
  name,
  prefixName = (n) => n,
  createWorksheetSelector,
  testType,
  visible = true,
  defaultSelection = Object.keys(associatedTestModel)[0] as AssociatedTestType,
  indented = true,
}) => {
  const dispatch = useDispatch();

  let associatedType = useSelector(createWorksheetSelector!(`${name}.AssociatedTestType`)) as AssociatedTestType;
  let [localType, setLocalType] = useState(associatedType);
  const typePath = name + AssociatedTestTypes[localType]?.path;

  useEffect(() => {
    // If the server has data of a specific type then we should ensure that we have that value set locally
    // If the server does not return a type, the use the local state as we may have selected to import
    if (hasValue(associatedType) && associatedType !== localType) setLocalType(associatedType);
  }, [associatedType, localType]);

  useEffect(() => {
    // On first load, if no associated test type is selected, set it to the default selection or first option available.
    if (!localType && !disabled) {
      dispatch(
        actions.setValue({ name: `${prefixName(name)}.AssociatedTestType`, value: defaultSelection, sync: true })
      );
    }
  }, [localType, disabled, defaultSelection, prefixName, name, dispatch]);

  const hasAssociatedTestData = useSelector(
    pipe(createWorksheetSelector!(typePath), (associatedTestData) => {
      return !hasEmptyValue(associatedTestData);
    })
  );

  const measuredTestQestId: number | undefined = useSelector(
    createWorksheetSelector!(`${name}.MeasuredTestOptions.SelectedTestQestId`)
  );

  let [lockMeasuredTestTypeList, setLockMeasuredTestTypeList] = useState(false);
  const [localMeasuredTestQestId, setLocalMeasuredTestQestId] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (hasValue(measuredTestQestId) && localMeasuredTestQestId !== measuredTestQestId) {
      setLocalMeasuredTestQestId(measuredTestQestId);
      setLockMeasuredTestTypeList(true);
    }
  }, [localMeasuredTestQestId, measuredTestQestId]);

  const hasAssociatedTestStarted = useSelector(
    pipe(createWorksheetSelector!(typePath), (associatedTestData) => {
      // If no associated test, test has not started
      if (isNil(localType) || hasEmptyValue(associatedTestData)) return false;

      const hasStartedFunctionMap = testTypeStartedFunctionMap[testType];
      if (!hasStartedFunctionMap) {
        throw new Error(`'testTypeStartedFunctionMap' configuration not defined for test type ${testType}.`);
      }
      return hasStartedFunctionMap[localType]?.(associatedTestData) ?? true;
    })
  );
  const handleAssociatedTestTypeChange = ({ data: { value: type } }: any) => {
    if (localType === type) return; // nothing changed
    setLocalType(type);
    setLocalMeasuredTestQestId(undefined);
    setLockMeasuredTestTypeList(false);
    dispatch(actions.setValue({ name: `${prefixName(name)}.ImportedTest`, value: undefined, sync: false }));
    dispatch(actions.setValue({ name: `${prefixName(name)}.AssociatedTestType`, value: type, sync: true }));
  };

  const modelHasType = (type: AssociatedTestType) => hasNonEmptyValue(associatedTestModel[type]);
  const getTypeOption = (value: AssociatedTestType, label: string | undefined) => {
    return { value: value, label: label ?? AssociatedTestTypes[value].label };
  };

  const measuredTestMethodOptions: ListItemModel[] = useSelector(
    pipe(
      createWorksheetSelector!(`${name}.MeasuredTestOptions.AllowedTestTypes`),
      (items) =>
        items?.map((item: any) => ({
          label: item.Label,
          value: item.Value,
        }))
    ),
    deepEquals
  );

  const handleMeasuredTestIdChange = ({ data: { value: newMeasuredTestQestIdValue } }: any) => {
    var newMeasuredTestQestId = parseInt(newMeasuredTestQestIdValue);
    if (measuredTestQestId === newMeasuredTestQestId) return;
    setLocalMeasuredTestQestId(newMeasuredTestQestId);
    dispatch(
      actions.setValue({
        name: `${prefixName(name)}.MeasuredTestOptions.SelectedTestQestId`,
        value: newMeasuredTestQestId,
        sync: true,
      })
    );
  };

  const options: Array<{ value: AssociatedTestType; label: string }> = [];
  if (modelHasType(AssociatedTestType.Measured))
    options.push(getTypeOption(AssociatedTestType.Measured, optionsLabels?.measured));
  if (modelHasType(AssociatedTestType.Imported))
    options.push(getTypeOption(AssociatedTestType.Imported, optionsLabels?.imported));
  if (modelHasType(AssociatedTestType.AutoImported))
    options.push(getTypeOption(AssociatedTestType.AutoImported, optionsLabels?.autoImported));
  if (modelHasType(AssociatedTestType.External))
    options.push(getTypeOption(AssociatedTestType.External, optionsLabels?.external));

  if (typeof heading === 'string') {
    heading = toTitleCase(heading);
  }

  let measuredProps = typeof associatedTestModel.measured === 'boolean' ? {} : associatedTestModel.measured;
  if (hasNonEmptyValue(measuredTestQestId)) {
    measuredProps = { ...measuredProps, testMethodId: `${measuredTestQestId}` };
  }
  const autoImportedProps =
    typeof associatedTestModel.autoImported === 'boolean'
      ? associatedTestModel.imported
      : { ...associatedTestModel.imported, ...associatedTestModel.autoImported };

  const associatedTestSection = () => {
    switch (localType) {
      case AssociatedTestType.Measured:
        if (hasNonEmptyValue(measuredTestQestId) || hasAssociatedTestData)
          return <WorksheetMeasuredTestSection name={typePath} {...measuredProps} />;
        return <></>;
      case AssociatedTestType.Imported:
        return <WorksheetImportedTestSection name={typePath} {...associatedTestModel.imported} />;
      case AssociatedTestType.AutoImported:
        return <WorksheetAutoImportedTestSection name={typePath} {...autoImportedProps} />;
      case AssociatedTestType.External:
        return <WorksheetExternalTestSection name={typePath} {...associatedTestModel.external} />;
    }
  };

  const displayMeasuredTestTypeList =
    localType === AssociatedTestType.Measured && hasNonEmptyValue(measuredTestMethodOptions);

  return !visible ? null : (
    <section
      className={classNames('WorksheetAssociatedTestSection', {
        'WorksheetAssociatedTestSection-indented': indented,
      })}
    >
      {heading && <h2 className="WorksheetAssociatedTestSection-Heading">{heading}</h2>}
      <div className="WorksheetAssociatedTestSection-Content">
        {options.length > 1 || displayMeasuredTestTypeList ? (
          <ListFieldSection wide={!displayMeasuredTestTypeList}>
            {options.length > 1 && (
              <Field
                input={<RadioGroup />}
                label={label}
                options={options}
                value={localType}
                onChange={handleAssociatedTestTypeChange}
                confirm={() => hasAssociatedTestStarted}
                confirmTitle="Confirm Determination Change"
                confirmText="Any existing data for this results determination will be removed."
                confirmButtonText="Change Determination"
              />
            )}
            {displayMeasuredTestTypeList &&
              (lockMeasuredTestTypeList ? (
                <FieldSet>
                  <Field
                    input={<FixedOptionTextOutput />}
                    label="Test Method"
                    options={measuredTestMethodOptions}
                    value={`${measuredTestQestId}`}
                  />
                  <ButtonGroup>
                    <ConfirmDialog
                      confirmTitle="Delete existing data?"
                      confirmText="Changing the measured test method will delete any currently entered test data. Do you wish to continue?"
                    >
                      <Button onClick={() => setLockMeasuredTestTypeList(false)}>Change</Button>
                    </ConfirmDialog>
                  </ButtonGroup>
                </FieldSet>
              ) : (
                <Field
                  input={<RadioGroup />}
                  label="Test Method"
                  onChange={handleMeasuredTestIdChange}
                  options={measuredTestMethodOptions}
                  value={`${measuredTestQestId}`}
                />
              ))}
          </ListFieldSection>
        ) : null}
        {associatedTestSection()}
      </div>
    </section>
  );
};

WorksheetAssociatedTestSection = withInjectedProps(WorksheetAssociatedTestSection);

export default WorksheetAssociatedTestSection;
